import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useDropzone } from 'react-dropzone';

interface Message {
  id: string;
  recipientName: string;
  recipientEmail?: string;
  createdByName: string;
  createdAt: string;
  expiresAt: string;
  maxReads: number;
  readCount: number;
  status: 'active' | 'read' | 'expired' | 'deleted';
  token?: string;
}

interface MessageDetail extends Message {
  content?: string;
  decryptedContent?: string;
  senderName?: string;
  senderEmail?: string;
  attachments?: { id: string; filename: string; size: number }[];
}

interface CreateMessageForm {
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
  content: string;
  expiresInHours: number;
  maxReads: number;
}

export default function MessagesPage() {
  const { graphAccessToken } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [createdExpiresAt, setCreatedExpiresAt] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MessageDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailCustomMessage, setEmailCustomMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [form, setForm] = useState<CreateMessageForm>({
    recipientName: '',
    recipientEmail: '',
    recipientPhone: '',
    content: '',
    expiresInHours: 168,
    maxReads: 1,
  });
  const [files, setFiles] = useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const currentSize = files.reduce((sum, f) => sum + f.size, 0);
    const newSize = acceptedFiles.reduce((sum, f) => sum + f.size, 0);

    if (currentSize + newSize > 100 * 1024 * 1024) {
      toast.error('La taille totale des fichiers ne peut pas depasser 100 Mo');
      return;
    }

    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 100 * 1024 * 1024,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const loadMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.listMessages({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setMessages(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleViewMessage = async (message: Message) => {
    setIsLoadingDetail(true);
    setSelectedMessage(message as MessageDetail);
    try {
      const response = await adminApi.getMessage(message.id);
      setSelectedMessage(response.data);
    } catch (error) {
      console.error('Failed to load message detail:', error);
      toast.error('Erreur lors du chargement du message');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCreateMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.recipientName.trim()) {
      toast.error('Le nom du destinataire est requis');
      return;
    }

    if (!form.content.trim()) {
      toast.error('Le message est requis');
      return;
    }

    setIsCreating(true);
    try {
      const response = await adminApi.createMessage({
        recipientName: form.recipientName.trim(),
        recipientEmail: form.recipientEmail.trim() || undefined,
        recipientPhone: form.recipientPhone.trim() || undefined,
        content: form.content.trim(),
        expiresInHours: form.expiresInHours,
        maxReads: form.maxReads,
        files: files.length > 0 ? files : undefined,
      });
      setCreatedUrl(response.data.fullUrl);
      setCreatedExpiresAt(response.data.expiresAt);
      toast.success('Message cree avec succes');
      loadMessages();
    } catch (error) {
      toast.error('Erreur lors de la creation du message');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyUrl = () => {
    if (createdUrl) {
      navigator.clipboard.writeText(createdUrl);
      toast.success('URL copiee dans le presse-papier');
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatedUrl(null);
    setCreatedExpiresAt(null);
    setShowEmailForm(false);
    setEmailCustomMessage('');
    setFiles([]);
    setForm({
      recipientName: '',
      recipientEmail: '',
      recipientPhone: '',
      content: '',
      expiresInHours: 168,
      maxReads: 1,
    });
  };

  const handleSendEmail = async () => {
    if (!form.recipientEmail) {
      toast.error('L\'email du destinataire est requis pour envoyer par email');
      return;
    }

    if (!graphAccessToken) {
      toast.error('Token Microsoft Graph non disponible. Veuillez vous reconnecter.');
      return;
    }

    if (!createdUrl || !createdExpiresAt) {
      toast.error('Informations du message manquantes');
      return;
    }

    setIsSendingEmail(true);
    try {
      await adminApi.sendSecureLinkEmail({
        recipientEmail: form.recipientEmail.trim(),
        recipientName: form.recipientName.trim(),
        messageUrl: createdUrl,
        expiresAt: createdExpiresAt,
        customMessage: emailCustomMessage.trim() || undefined,
        graphAccessToken,
      });
      toast.success('Email envoye avec succes');
      setShowEmailForm(false);
    } catch (error: any) {
      console.error('Failed to send email:', error);
      if (error.response?.status === 403) {
        toast.error('Permission Mail.Send non accordee. Verifiez les autorisations de l\'application.');
      } else {
        toast.error('Erreur lors de l\'envoi de l\'email');
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleRevokeMessage = async (id: string) => {
    if (!confirm('Etes-vous sur de vouloir revoquer ce message ?')) return;

    try {
      await adminApi.deleteMessage(id);
      toast.success('Message revoque');
      loadMessages();
    } catch (error) {
      toast.error('Erreur lors de la revocation');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">Actif</span>;
      case 'read':
        return <span className="badge badge-info">Lu</span>;
      case 'expired':
        return <span className="badge badge-warning">Expire</span>;
      case 'deleted':
        return <span className="badge badge-error">Revoque</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Messages envoyes</h1>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouveau message
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            className="input w-auto"
            value={statusFilter}
            aria-label="Filtrer par statut"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="read">Lu</option>
            <option value="expired">Expire</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner spinner-lg" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Destinataire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cree le
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Expire le
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Lectures
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {messages.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400">Aucun message envoye pour le moment</p>
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                        className="btn-primary mt-4"
                      >
                        Creer un premier message
                      </button>
                    </td>
                  </tr>
                ) : (
                  messages.map((message) => (
                    <tr key={message.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{message.recipientName}</div>
                        {message.recipientEmail && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{message.recipientEmail}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(message.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(message.expiresAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {message.readCount} / {message.maxReads}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(message.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                        <button
                          type="button"
                          onClick={() => handleViewMessage(message)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          Voir
                        </button>
                        {message.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => handleRevokeMessage(message.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Revoquer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-outline"
            >
              Precedent
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} sur {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-outline"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* View Message Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Detail du message
                </h2>
                <button
                  type="button"
                  onClick={() => setSelectedMessage(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Fermer"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner spinner-lg" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Message info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Destinataire</label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedMessage.recipientName}</p>
                      {selectedMessage.recipientEmail && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{selectedMessage.recipientEmail}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Statut</label>
                      <div className="mt-1">{getStatusBadge(selectedMessage.status)}</div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cree le</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedMessage.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Expire le</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedMessage.expiresAt)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lectures</label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedMessage.readCount} / {selectedMessage.maxReads}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cree par</label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedMessage.senderName || selectedMessage.createdByName || '-'}</p>
                    </div>
                  </div>

                  {/* Message content */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Contenu du message</label>
                    <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      {selectedMessage.decryptedContent || selectedMessage.content ? (
                        <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-sans">
                          {selectedMessage.decryptedContent || selectedMessage.content}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                          Contenu chiffre - non disponible pour visualisation
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pieces jointes</label>
                      <ul className="mt-2 space-y-2">
                        {selectedMessage.attachments.map((attachment) => (
                          <li key={attachment.id} className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {attachment.filename}
                            <span className="text-gray-500 dark:text-gray-400">
                              ({(attachment.size / 1024).toFixed(1)} Ko)
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 flex justify-end gap-3">
                    {selectedMessage.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => {
                          handleRevokeMessage(selectedMessage.id);
                          setSelectedMessage(null);
                        }}
                        className="btn-danger"
                      >
                        Revoquer ce message
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setSelectedMessage(null)}
                      className="btn-secondary"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {createdUrl ? 'Message cree' : 'Nouveau message securise'}
                </h2>
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Fermer"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createdUrl ? (
                /* Success view */
                <div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Votre message securise a ete cree. Partagez ce lien avec le destinataire :
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-4">
                      <code className="text-sm break-all text-gray-900 dark:text-white">{createdUrl}</code>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 mb-4">
                      <button
                        type="button"
                        onClick={handleCopyUrl}
                        className="btn-primary"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copier le lien
                      </button>
                      {form.recipientEmail && (
                        <button
                          type="button"
                          onClick={() => setShowEmailForm(!showEmailForm)}
                          className="btn-secondary"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Envoyer par email
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleCloseCreateModal}
                        className="btn-outline"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>

                  {/* Email form */}
                  {showEmailForm && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        Envoyer le lien par email
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="label text-sm">Destinataire</label>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {form.recipientName} ({form.recipientEmail})
                          </p>
                        </div>
                        <div>
                          <label className="label text-sm">Message personnalise (optionnel)</label>
                          <textarea
                            className="input text-sm"
                            placeholder="Ajoutez un message personnel..."
                            value={emailCustomMessage}
                            onChange={(e) => setEmailCustomMessage(e.target.value)}
                            rows={3}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Ce message apparaitra dans l'email avant le lien securise.
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowEmailForm(false)}
                            className="btn-outline text-sm"
                            disabled={isSendingEmail}
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={handleSendEmail}
                            className="btn-primary text-sm"
                            disabled={isSendingEmail}
                          >
                            {isSendingEmail ? (
                              <span className="flex items-center">
                                <span className="spinner w-4 h-4 mr-2" />
                                Envoi...
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Envoyer l'email
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Form view */
                <form onSubmit={handleCreateMessage} className="space-y-4">
                  <div>
                    <label className="label">Nom du destinataire *</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="Jean Dupont"
                      value={form.recipientName}
                      onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Email du destinataire</label>
                    <input
                      type="email"
                      className="input"
                      placeholder="jean.dupont@exemple.fr"
                      value={form.recipientEmail}
                      onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Telephone du destinataire</label>
                    <input
                      type="tel"
                      className="input"
                      placeholder="+33 6 12 34 56 78"
                      value={form.recipientPhone}
                      onChange={(e) => setForm({ ...form, recipientPhone: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Requis si la verification SMS est activee
                    </p>
                  </div>
                  <div>
                    <label className="label">Message *</label>
                    <textarea
                      className="input min-h-[120px]"
                      placeholder="Votre message confidentiel..."
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="expiresInHours" className="label">Expiration (heures)</label>
                      <input
                        type="number"
                        id="expiresInHours"
                        className="input"
                        value={form.expiresInHours}
                        onChange={(e) => setForm({ ...form, expiresInHours: parseInt(e.target.value) || 168 })}
                        min={1}
                        max={720}
                      />
                    </div>
                    <div>
                      <label htmlFor="maxReads" className="label">Lectures max</label>
                      <input
                        type="number"
                        id="maxReads"
                        className="input"
                        value={form.maxReads}
                        onChange={(e) => setForm({ ...form, maxReads: parseInt(e.target.value) || 1 })}
                        min={1}
                        max={10}
                      />
                    </div>
                  </div>

                  {/* File attachments */}
                  <div>
                    <label className="label">Pieces jointes (optionnel)</label>
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                        isDragActive
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {isDragActive
                          ? 'Deposez les fichiers ici...'
                          : 'Glissez des fichiers ou cliquez pour selectionner'}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Max 100 Mo au total
                      </p>
                    </div>

                    {/* File list */}
                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center min-w-0">
                              <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="text-sm text-gray-900 dark:text-white truncate">{file.name}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-2"
                              aria-label="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseCreateModal}
                      className="btn-secondary"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <span className="flex items-center">
                          <span className="spinner mr-2" />
                          Creation...
                        </span>
                      ) : (
                        'Generer le lien'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
